import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../components/admin/AdminLayout';
import {
  Layers, RefreshCw, Shield, AlertTriangle, CheckCircle2,
  ChevronDown, ChevronRight, Trash2, FileSearch, X, Users,
  Ghost, HelpCircle, FileText, Clock, Search,
} from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

const SCOPES = [
  { value: 'all', label: 'All' },
  { value: 'real_users', label: 'Real Users' },
  { value: 'anonymous', label: 'Anonymous' },
  { value: 'ownerless', label: 'Ownerless' },
  { value: 'zombie', label: 'Zombie' },
  { value: 'default_unmodified', label: 'Default/Unmodified' },
  { value: 'edited_anonymous', label: 'Edited Anonymous' },
];

const RISK_BADGE = {
  protected: { bg: '#1e3a5f', border: '#3b82f6', color: '#93c5fd', label: 'PROTECTED', icon: Shield },
  review_required: { bg: '#4a3520', border: '#f59e0b', color: '#fbbf24', label: 'REVIEW', icon: AlertTriangle },
  safe_candidate: { bg: '#1a3a2a', border: '#22c55e', color: '#86efac', label: 'SAFE', icon: CheckCircle2 },
};

function RiskBadge({ level }) {
  const cfg = RISK_BADGE[level] || RISK_BADGE.review_required;
  const Icon = cfg.icon;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
      background: cfg.bg, border: `1px solid ${cfg.border}`, color: cfg.color,
      fontFamily: "'IBM Plex Mono', monospace",
    }}>
      <Icon size={11} />{cfg.label}
    </span>
  );
}

function SummaryCard({ label, value, icon: Icon, color = '#a1a1aa' }) {
  return (
    <div style={{
      background: '#1a1a1c', border: '1px solid #2a2a2e', borderRadius: 8,
      padding: '14px 16px', flex: '1 1 140px', minWidth: 140,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <Icon size={14} style={{ color }} />
        <span style={{ color: '#71717a', fontSize: 11, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: '#e4e4e7', fontFamily: "'IBM Plex Mono', monospace" }}>
        {value ?? '—'}
      </div>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return '1d ago';
    if (diffDays < 30) return `${diffDays}d ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`;
    return `${Math.floor(diffDays / 365)}y ago`;
  } catch { return '—'; }
}

export default function AdminCarousels() {
  const [password, setPassword] = useState(() => sessionStorage.getItem('admin_password') || '');
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [audit, setAudit] = useState(null);
  const [listData, setListData] = useState(null);
  const [scope, setScope] = useState('all');
  const [selected, setSelected] = useState(new Set());
  const [manifest, setManifest] = useState(null);
  const [showManifest, setShowManifest] = useState(false);
  const [confirmation, setConfirmation] = useState('');
  const [executing, setExecuting] = useState(false);
  const [executeResult, setExecuteResult] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const headers = { 'X-Admin-Password': password, 'Content-Type': 'application/json' };

  const doLogin = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND}/api/admin/carousels/audit`, { headers: { 'X-Admin-Password': password } });
      if (res.status === 401) { setError('Invalid password'); return; }
      if (!res.ok) { setError(`Error: ${res.status}`); return; }
      sessionStorage.setItem('admin_password', password);
      setAuthed(true);
      setError(null);
    } catch (e) { setError(e.message); }
  }, [password]);

  const fetchAudit = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND}/api/admin/carousels/audit`, { headers });
      if (!res.ok) throw new Error(`${res.status}`);
      setAudit(await res.json());
    } catch (e) { setError(e.message); }
    setLoading(false);
  }, [password]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND}/api/admin/carousels/list?scope=${scope}&limit=200`, { headers });
      if (!res.ok) throw new Error(`${res.status}`);
      const data = await res.json();
      setListData(data);
      setSelected(new Set());
    } catch (e) { setError(e.message); }
    setLoading(false);
  }, [password, scope]);

  useEffect(() => {
    if (authed) { fetchAudit(); fetchList(); }
  }, [authed, scope]);

  const refreshAll = () => { fetchAudit(); fetchList(); setExecuteResult(null); };

  const handleSelectAll = (items) => {
    const selectable = items.filter(i => i.risk_level !== 'protected');
    if (selected.size === selectable.length && selectable.length > 0) {
      setSelected(new Set());
    } else {
      setSelected(new Set(selectable.map(i => i.carousel_id)));
    }
  };

  const toggleSelect = (id, riskLevel) => {
    if (riskLevel === 'protected') return;
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const generateManifest = async () => {
    if (selected.size === 0) return;
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND}/api/admin/carousels/delete-manifest`, {
        method: 'POST', headers,
        body: JSON.stringify({ carousel_ids: [...selected], reason: 'stale_anonymous_cleanup' }),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      const data = await res.json();
      setManifest(data);
      setShowManifest(true);
      setConfirmation('');
      setExecuteResult(null);
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const executeDelete = async () => {
    if (!manifest || confirmation !== 'DELETE STALE CAROUSELS') return;
    setExecuting(true);
    try {
      const ids = manifest.deletable.map(d => d.carousel_id);
      if (ids.length === 0) { setError('No deletable carousels in manifest'); setExecuting(false); return; }
      const res = await fetch(`${BACKEND}/api/admin/carousels/delete-execute`, {
        method: 'POST', headers,
        body: JSON.stringify({
          carousel_ids: ids,
          reason: manifest.reason || 'stale_anonymous_cleanup',
          confirmation: 'DELETE STALE CAROUSELS',
        }),
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || `${res.status}`); }
      const result = await res.json();
      setExecuteResult(result);
      setShowManifest(false);
      setManifest(null);
      setSelected(new Set());
      setConfirmation('');
      refreshAll();
    } catch (e) { setError(e.message); }
    setExecuting(false);
  };

  const handleLogout = () => {
    sessionStorage.removeItem('admin_password');
    setAuthed(false);
    setPassword('');
  };

  // ── Login ──
  if (!authed) {
    return (
      <div style={{ background: '#111113', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ background: '#1a1a1c', border: '1px solid #2a2a2e', borderRadius: 12, padding: 32, width: 340 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
            <Layers size={20} style={{ color: '#a78bfa' }} />
            <span style={{ color: '#e4e4e7', fontSize: 18, fontWeight: 700 }}>Carousel Inventory</span>
          </div>
          {error && <div style={{ color: '#f87171', fontSize: 13, marginBottom: 12 }}>{error}</div>}
          <input
            type="password" placeholder="Admin password" value={password}
            onChange={e => setPassword(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && doLogin()}
            style={{ width: '100%', padding: '10px 12px', background: '#111113', border: '1px solid #3f3f46', borderRadius: 6, color: '#e4e4e7', fontSize: 14, marginBottom: 12, boxSizing: 'border-box' }}
          />
          <button onClick={doLogin} style={{ width: '100%', padding: '10px 0', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
            Sign In
          </button>
        </div>
      </div>
    );
  }

  const items = listData?.items || [];
  const filteredItems = searchTerm
    ? items.filter(i =>
        i.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        i.carousel_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        i.owner_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        i.owner_id?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : items;

  const selectableItems = filteredItems.filter(i => i.risk_level !== 'protected');

  return (
    <AdminLayout activePage="carousels" onLogout={handleLogout}>
      <div style={{ padding: '24px 28px', maxWidth: 1300, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Layers size={22} style={{ color: '#a78bfa' }} />
            <h1 style={{ color: '#e4e4e7', fontSize: 22, fontWeight: 700, margin: 0 }}>Carousel Inventory</h1>
          </div>
          <button onClick={refreshAll} disabled={loading} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: '#27272a', border: '1px solid #3f3f46', borderRadius: 6, color: '#a1a1aa', fontSize: 13, cursor: 'pointer' }}>
            <RefreshCw size={14} className={loading ? 'spin' : ''} /> Refresh
          </button>
        </div>

        {error && (
          <div style={{ background: '#3b1111', border: '1px solid #7f1d1d', borderRadius: 8, padding: '10px 14px', marginBottom: 16, color: '#fca5a5', fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{error}</span>
            <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', color: '#fca5a5', cursor: 'pointer' }}><X size={14} /></button>
          </div>
        )}

        {/* Execute Result Banner */}
        {executeResult && (
          <div style={{ background: '#0a2e1a', border: '1px solid #16a34a', borderRadius: 8, padding: '14px 18px', marginBottom: 16 }}>
            <div style={{ color: '#86efac', fontSize: 15, fontWeight: 600, marginBottom: 8 }}>
              <CheckCircle2 size={16} style={{ display: 'inline', verticalAlign: -3, marginRight: 6 }} />
              Deletion Complete
            </div>
            <div style={{ color: '#a7f3d0', fontSize: 13, lineHeight: 1.6 }}>
              Deleted: {executeResult.summary?.deleted_count || 0} · 
              Skipped: {executeResult.summary?.skipped_count || 0} · 
              Protected: {executeResult.summary?.protected_count || 0} · 
              Assets orphaned: {executeResult.summary?.assets_marked_orphaned || 0} · 
              Assets kept active: {executeResult.summary?.assets_kept_active_because_shared || 0}
            </div>
            <div style={{ color: '#6ee7b7', fontSize: 12, marginTop: 6, fontStyle: 'italic' }}>
              {executeResult.s3_reminder}
            </div>
          </div>
        )}

        {/* Summary Cards */}
        {audit && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 20 }}>
            <SummaryCard label="Total Carousels" value={audit.totals.total_carousels} icon={Layers} color="#a78bfa" />
            <SummaryCard label="Total Slides" value={audit.totals.total_slides} icon={FileText} color="#60a5fa" />
            <SummaryCard label="Protected" value={audit.totals.protected} icon={Shield} color="#3b82f6" />
            <SummaryCard label="Safe Candidates" value={audit.totals.safe_candidate} icon={CheckCircle2} color="#22c55e" />
            <SummaryCard label="Review Required" value={audit.totals.review_required} icon={AlertTriangle} color="#f59e0b" />
          </div>
        )}

        {/* Groups breakdown */}
        {audit && Object.keys(audit.groups).length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <GroupBreakdown groups={audit.groups} ageBuckets={audit.age_buckets} />
          </div>
        )}

        {/* Scope Tabs */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 14, flexWrap: 'wrap' }}>
          {SCOPES.map(s => (
            <button key={s.value} onClick={() => setScope(s.value)}
              style={{
                padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                border: scope === s.value ? '1px solid #7c3aed' : '1px solid #3f3f46',
                background: scope === s.value ? '#2e1065' : '#1a1a1c',
                color: scope === s.value ? '#c4b5fd' : '#a1a1aa',
              }}>{s.label}
              {listData && scope === s.value ? ` (${listData.total_count})` : ''}
            </button>
          ))}
        </div>

        {/* Search + Bulk Actions */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: '1 1 200px', maxWidth: 320 }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: 10, color: '#71717a' }} />
            <input
              placeholder="Search by name, ID, owner…"
              value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              style={{ width: '100%', padding: '8px 10px 8px 30px', background: '#111113', border: '1px solid #3f3f46', borderRadius: 6, color: '#e4e4e7', fontSize: 13, boxSizing: 'border-box' }}
            />
          </div>
          {selected.size > 0 && (
            <>
              <span style={{ color: '#a1a1aa', fontSize: 12 }}>{selected.size} selected</span>
              <button onClick={generateManifest} disabled={loading} style={{
                display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px',
                background: '#4a1d1d', border: '1px solid #991b1b', borderRadius: 6,
                color: '#fca5a5', fontSize: 12, fontWeight: 600, cursor: 'pointer',
              }}>
                <FileSearch size={13} /> Generate Delete Manifest
              </button>
            </>
          )}
        </div>

        {/* Table */}
        <div style={{ overflowX: 'auto', border: '1px solid #2a2a2e', borderRadius: 8, background: '#141416' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #2a2a2e' }}>
                <th style={thStyle}>
                  <input type="checkbox" checked={selectableItems.length > 0 && selected.size === selectableItems.length}
                    onChange={() => handleSelectAll(filteredItems)}
                    style={{ accentColor: '#7c3aed' }} />
                </th>
                <th style={thStyle}>Name</th>
                <th style={thStyle}>Owner</th>
                <th style={thStyle}>Anon?</th>
                <th style={thStyle}>Slides</th>
                <th style={thStyle}>Doc Assets</th>
                <th style={thStyle}>Refs</th>
                <th style={thStyle}>Version</th>
                <th style={thStyle}>Updated</th>
                <th style={thStyle}>Risk</th>
                <th style={thStyle}>Reason</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.length === 0 ? (
                <tr><td colSpan={11} style={{ padding: 24, textAlign: 'center', color: '#71717a' }}>
                  {loading ? 'Loading…' : 'No carousels found for this scope'}
                </td></tr>
              ) : filteredItems.map(item => {
                const isProtected = item.risk_level === 'protected';
                const isSelected = selected.has(item.carousel_id);
                return (
                  <tr key={item.carousel_id}
                    onClick={() => toggleSelect(item.carousel_id, item.risk_level)}
                    style={{
                      borderBottom: '1px solid #1f1f23',
                      cursor: isProtected ? 'default' : 'pointer',
                      background: isSelected ? '#1e1b4b' : 'transparent',
                      opacity: isProtected ? 0.7 : 1,
                    }}>
                    <td style={tdStyle}>
                      <input type="checkbox" checked={isSelected} disabled={isProtected}
                        onChange={() => toggleSelect(item.carousel_id, item.risk_level)}
                        style={{ accentColor: '#7c3aed' }} />
                    </td>
                    <td style={{ ...tdStyle, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <div style={{ color: '#e4e4e7', fontWeight: 500 }}>{item.name}</div>
                      <div style={{ color: '#52525b', fontSize: 10, fontFamily: "'IBM Plex Mono', monospace" }}>{item.carousel_id.slice(0, 12)}…</div>
                    </td>
                    <td style={tdStyle}>
                      <div style={{ color: '#a1a1aa', fontSize: 12 }}>{item.owner_email || item.owner_id?.slice(0, 8) || '—'}</div>
                    </td>
                    <td style={tdStyle}>
                      {item.is_anonymous === true ? <Ghost size={14} style={{ color: '#a78bfa' }} /> :
                       item.is_anonymous === false ? <Users size={14} style={{ color: '#3b82f6' }} /> :
                       <HelpCircle size={14} style={{ color: '#525252' }} />}
                    </td>
                    <td style={{ ...tdStyle, fontFamily: "'IBM Plex Mono', monospace" }}>{item.slide_count}</td>
                    <td style={{ ...tdStyle, fontFamily: "'IBM Plex Mono', monospace" }}>{item.document_asset_id_count}</td>
                    <td style={{ ...tdStyle, fontFamily: "'IBM Plex Mono', monospace" }}>{item.asset_reference_count}</td>
                    <td style={{ ...tdStyle, fontFamily: "'IBM Plex Mono', monospace" }}>{item.version}</td>
                    <td style={{ ...tdStyle, fontSize: 11, color: '#71717a' }}>{formatDate(item.updated_at)}</td>
                    <td style={tdStyle}><RiskBadge level={item.risk_level} /></td>
                    <td style={{ ...tdStyle, fontSize: 11, color: '#a1a1aa', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.reason}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {listData && (
          <div style={{ color: '#52525b', fontSize: 11, marginTop: 8 }}>
            Showing {filteredItems.length} of {listData.total_count} carousels (scope: {scope})
          </div>
        )}

        {/* Manifest Modal */}
        {showManifest && manifest && (
          <ManifestModal
            manifest={manifest}
            confirmation={confirmation}
            setConfirmation={setConfirmation}
            onExecute={executeDelete}
            onClose={() => { setShowManifest(false); setConfirmation(''); }}
            executing={executing}
          />
        )}
      </div>
    </AdminLayout>
  );
}

// ── Group breakdown ──

function GroupBreakdown({ groups, ageBuckets }) {
  const [expanded, setExpanded] = useState(false);
  const groupEntries = Object.entries(groups);

  const CATEGORY_ICONS = {
    protected: Shield,
    anonymous: Ghost,
    edited_anonymous: AlertTriangle,
    ownerless: HelpCircle,
    zombie: Ghost,
    default_unmodified: CheckCircle2,
  };

  return (
    <div style={{ background: '#1a1a1c', border: '1px solid #2a2a2e', borderRadius: 8, overflow: 'hidden' }}>
      <button onClick={() => setExpanded(!expanded)} style={{
        display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '12px 16px',
        background: 'transparent', border: 'none', color: '#a1a1aa', fontSize: 13, fontWeight: 600, cursor: 'pointer', textAlign: 'left',
      }}>
        {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        Group Breakdown ({groupEntries.length} categories)
        {ageBuckets && (
          <span style={{ color: '#52525b', fontSize: 11, fontWeight: 400, marginLeft: 'auto' }}>
            Age: {Object.entries(ageBuckets).filter(([,v]) => v > 0).map(([k,v]) => `${k}:${v}`).join(' · ') || 'none non-protected'}
          </span>
        )}
      </button>
      {expanded && (
        <div style={{ padding: '0 16px 14px' }}>
          {groupEntries.map(([key, g]) => {
            const Icon = CATEGORY_ICONS[key] || HelpCircle;
            return (
              <div key={key} style={{ borderTop: '1px solid #27272a', padding: '10px 0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <Icon size={14} style={{ color: key === 'protected' ? '#3b82f6' : '#a78bfa' }} />
                  <span style={{ color: '#e4e4e7', fontSize: 13, fontWeight: 600, textTransform: 'capitalize' }}>
                    {key.replace(/_/g, ' ')}
                  </span>
                  <span style={{ color: '#71717a', fontSize: 12 }}>
                    {g.carousel_count} carousels · {g.slide_count} slides · {g.document_asset_id_count} doc assets · {g.asset_reference_count} refs
                  </span>
                </div>
                {g.owners && g.owners.length > 0 && (
                  <div style={{ color: '#52525b', fontSize: 11, marginLeft: 22 }}>
                    Owners: {g.owners.map(o => o.email || o.owner_id?.slice(0, 8)).join(', ')}
                  </div>
                )}
                {g.samples && g.samples.length > 0 && (
                  <div style={{ color: '#52525b', fontSize: 11, marginLeft: 22, marginTop: 2 }}>
                    Samples: {g.samples.map(s => `"${s.name}" v${s.version}`).join(', ')}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Manifest Modal ──

function ManifestModal({ manifest, confirmation, setConfirmation, onExecute, onClose, executing }) {
  const s = manifest.summary;
  const canExecute = confirmation === 'DELETE STALE CAROUSELS' && s.deletable_count > 0 && !executing;

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }} onClick={onClose}>
      <div style={{
        background: '#1a1a1c', border: '1px solid #3f3f46', borderRadius: 12,
        padding: 28, maxWidth: 600, width: '90%', maxHeight: '80vh', overflowY: 'auto',
      }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <h2 style={{ color: '#e4e4e7', fontSize: 18, fontWeight: 700, margin: 0 }}>Delete Manifest Review</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#71717a', cursor: 'pointer' }}><X size={18} /></button>
        </div>

        {/* Summary */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
          <div style={statBox}><span style={statLabel}>Selected</span><span style={statVal}>{s.total_selected}</span></div>
          <div style={statBox}><span style={statLabel}>Deletable</span><span style={{ ...statVal, color: s.deletable_count > 0 ? '#fca5a5' : '#71717a' }}>{s.deletable_count}</span></div>
          <div style={statBox}><span style={statLabel}>Protected (skipped)</span><span style={{ ...statVal, color: '#93c5fd' }}>{s.protected_count}</span></div>
          <div style={statBox}><span style={statLabel}>Not Found</span><span style={statVal}>{s.not_found_count}</span></div>
          <div style={statBox}><span style={statLabel}>Slides to Delete</span><span style={statVal}>{s.total_slides_to_delete}</span></div>
          <div style={statBox}><span style={statLabel}>Affected Assets</span><span style={statVal}>{s.total_affected_assets}</span></div>
          <div style={statBox}><span style={statLabel}>Would Become Orphaned</span><span style={{ ...statVal, color: '#fbbf24' }}>{s.assets_would_become_orphaned}</span></div>
          <div style={statBox}><span style={statLabel}>Kept Active (shared)</span><span style={{ ...statVal, color: '#86efac' }}>{s.assets_shared_kept_active}</span></div>
        </div>

        {/* Warnings */}
        {manifest.warnings && manifest.warnings.length > 0 && (
          <div style={{ background: '#3b2815', border: '1px solid #92400e', borderRadius: 6, padding: '10px 12px', marginBottom: 14 }}>
            <div style={{ color: '#fbbf24', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Warnings</div>
            {manifest.warnings.map((w, i) => (
              <div key={i} style={{ color: '#fde68a', fontSize: 11, lineHeight: 1.5 }}>· {w}</div>
            ))}
          </div>
        )}

        {/* S3 Warning */}
        <div style={{ background: '#1e1b4b', border: '1px solid #4338ca', borderRadius: 6, padding: '10px 12px', marginBottom: 14 }}>
          <div style={{ color: '#c4b5fd', fontSize: 12, lineHeight: 1.5 }}>
            <strong>⚠ Important:</strong> {manifest.s3_warning}
          </div>
        </div>

        {/* Deletable list */}
        {manifest.deletable.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ color: '#a1a1aa', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Carousels to Delete:</div>
            {manifest.deletable.map(d => (
              <div key={d.carousel_id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: 12 }}>
                <RiskBadge level={d.risk_level} />
                <span style={{ color: '#e4e4e7' }}>"{d.name}"</span>
                <span style={{ color: '#52525b' }}>v{d.version} · {d.slide_count} slides · {d.affected_asset_ids.length} assets</span>
              </div>
            ))}
          </div>
        )}

        {/* Protected list */}
        {manifest.protected.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ color: '#93c5fd', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Protected (will NOT be deleted):</div>
            {manifest.protected.map(p => (
              <div key={p.carousel_id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: 12 }}>
                <RiskBadge level="protected" />
                <span style={{ color: '#e4e4e7' }}>"{p.name}"</span>
                <span style={{ color: '#52525b' }}>{p.reason}</span>
              </div>
            ))}
          </div>
        )}

        {/* Confirmation */}
        {s.deletable_count > 0 && (
          <div style={{ borderTop: '1px solid #27272a', paddingTop: 14, marginTop: 14 }}>
            <div style={{ color: '#a1a1aa', fontSize: 12, marginBottom: 8 }}>
              Type <code style={{ color: '#fca5a5', background: '#2a1515', padding: '2px 6px', borderRadius: 3 }}>DELETE STALE CAROUSELS</code> to confirm:
            </div>
            <input
              value={confirmation} onChange={e => setConfirmation(e.target.value)}
              placeholder="Type confirmation here…"
              style={{ width: '100%', padding: '10px 12px', background: '#111113', border: `1px solid ${confirmation === 'DELETE STALE CAROUSELS' ? '#22c55e' : '#3f3f46'}`, borderRadius: 6, color: '#e4e4e7', fontSize: 14, fontFamily: "'IBM Plex Mono', monospace", marginBottom: 12, boxSizing: 'border-box' }}
            />
            <button onClick={onExecute} disabled={!canExecute} style={{
              width: '100%', padding: '10px 0', borderRadius: 6, border: 'none', fontSize: 14, fontWeight: 700, cursor: canExecute ? 'pointer' : 'not-allowed',
              background: canExecute ? '#991b1b' : '#27272a', color: canExecute ? '#fff' : '#52525b',
            }}>
              {executing ? 'Executing…' : `Delete ${s.deletable_count} Carousel${s.deletable_count !== 1 ? 's' : ''}`}
            </button>
          </div>
        )}

        {s.deletable_count === 0 && (
          <div style={{ textAlign: 'center', padding: 16, color: '#71717a', fontSize: 13 }}>
            No carousels eligible for deletion. All selected carousels are protected.
          </div>
        )}
      </div>
    </div>
  );
}

// ── Styles ──

const thStyle = {
  padding: '10px 12px', textAlign: 'left', color: '#71717a', fontSize: 11,
  fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em',
  whiteSpace: 'nowrap', borderBottom: '1px solid #2a2a2e',
};

const tdStyle = {
  padding: '10px 12px', color: '#d4d4d8', whiteSpace: 'nowrap',
};

const statBox = {
  background: '#111113', borderRadius: 6, padding: '8px 12px',
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
};

const statLabel = { color: '#71717a', fontSize: 12 };
const statVal = { color: '#e4e4e7', fontSize: 16, fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace" };
